import { useEffect, useMemo } from 'react';

import { MenuItem } from '~/components/Layout/Drawer/types';
import { PAGES } from '~/constants/pages.constants';
import { useCurrentOrganization } from '~/hooks/organization/useCurrentOrganization';
import { useAppDispatch } from '~/hooks/useAppDispatch';
import { useAppSelector } from '~/hooks/useAppSelector';
import { useQuery } from '~/hooks/useQuery';
import { insightPosthogSliceActions } from '~/store/slices/insightPosthog';

interface InsightPosthog {
  organizationId: string;
  ecosystemId: string;
  embedUrl: string;
  _id: string;
}

const getMenuItems = ({
  isCompany = false,
}: {
  isCompany?: boolean;
}): MenuItem[] => {
  const menuItems = [
    {
      icon: 'calendar',
      label: 'Activities',
      path: PAGES.ACTIVITIES,
    },
    {
      icon: 'users',
      label: 'Network',
      path: PAGES.NETWORK,
    },
  ];

  if (isCompany) {
    menuItems.unshift({
      icon: 'home',
      label: 'Home',
      path: PAGES.HOME,
    });

    menuItems.push({
      icon: 'bar-chart-2',
      label: 'Insights',
      path: PAGES.INSIGHTS,
    });
  }

  return menuItems;
};

export const useDynamicMenuItems = () => {
  const { isNFP } = useCurrentOrganization();
  const dispatch = useAppDispatch();

  const { selectedEcosystem } = useAppSelector(({ ecosystem }) => ecosystem);

  const { data: insightPosthog } = useQuery<InsightPosthog>(
    `insight-posthog/ecosystem/${selectedEcosystem?._id}`,
    {
      key: `insight-posthog-ecosystem-${selectedEcosystem?._id}`,
      queryOptions: {
        enabled: !!selectedEcosystem?._id,
      },
    },
  );

  const baseMenuItems = useMemo(() => {
    return getMenuItems({ isCompany: !isNFP });
  }, [isNFP]);

  const dynamicMenuItems = useMemo(() => {
    const items: MenuItem[] = [...baseMenuItems];

    if (
      insightPosthog &&
      !items.some((item) => item.path === PAGES.INSIGHT_POSTHOG)
    ) {
      items.push({
        icon: 'pie-chart',
        label: 'Report',
        path: PAGES.INSIGHT_POSTHOG,
      });
    }

    return items;
  }, [baseMenuItems, insightPosthog]);

  useEffect(() => {
    if (insightPosthog) {
      dispatch(
        insightPosthogSliceActions.setInsightPosthog({
          organizationId: insightPosthog.organizationId,
          ecosystemId: insightPosthog.ecosystemId,
          embedUrl: insightPosthog.embedUrl,
          _id: insightPosthog._id,
        }),
      );
    }
  }, [insightPosthog, dispatch]);

  return {
    dynamicMenuItems,
  };
};
